import { SvgIconComponent } from '@mui/icons-material'
import InfoOutlined from '@mui/icons-material/InfoOutlined'
import {
  IconOwnProps,
  IconProps,
  SxProps,
  Theme,
  Tooltip,
  TooltipProps,
  Typography,
} from '@mui/material'
import { FC, ReactElement } from 'react'
import LoadedLink from './LoadedLink'

export type LoadedTooltipProps = {
  Icon?: SvgIconComponent
  iconProps?: IconProps
  text?: string
  url?: string
  openNewTab?: boolean
  maxWidth?: number
  typographySxProps?: SxProps<Theme>
  /** Re-declared as optional because TooltipProps requires it, but this component
   * falls back to rendering an icon or truncated text when children is not provided. */
  children?: ReactElement
} & Omit<TooltipProps, 'children'>

/**
 * Displays a consistent tooltip across the app, either on an icon or on truncated text.
 *
 * - If `Icon` is provided, renders the icon with the tooltip message.
 * - If `text` is provided, renders ellipsed text (with optional `url` link) and shows the tooltip on hover.
 * - Supports all standard tooltip features like placement, arrow, and interaction behavior.
 *
 * @param {SvgIconComponent} [props.Icon] - Optional icon component to render inside the tooltip.
 * @param {IconProps} [props.iconProps] - Props passed to the icon component.
 * @param {string} [props.text] - Text content to display with ellipsis and tooltip.
 * @param {string} [props.url] - Optional URL for wrapping the text in a link.
 * @param {boolean} [props.openNewTab=true] - Whether the link opens in a new tab.
 * @param {number} [props.maxWidth=150] - Max width of the truncated text container.
 * @param {SxProps<Theme>} [props.typographySxProps] - Styles applied to the typography component.
 *
 * @returns {JSX.Element} The rendered tooltip component with either icon or truncated text content.
 */
const LoadedTooltip: FC<LoadedTooltipProps> = ({
  arrow = true,
  Icon = InfoOutlined,
  iconProps,
  text,
  url,
  maxWidth = 150,
  openNewTab = true,
  typographySxProps,
  children,
  ...tooltipProps
}) => {
  const ellipsisStyle = {
    display: 'inline-block',
    maxWidth: maxWidth ?? 150,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    verticalAlign: 'bottom',
    lineHeight: '100%',
  }

  const renderTooltipContent = () => {
    if (!text) {
      return (
        <Icon
          sx={{
            ...iconProps?.sx,
            cursor: 'help',
          }}
          color={iconProps?.color ?? 'disabled'}
          className={'LoadedIcon-with-tooltip'}
          {...(iconProps as IconOwnProps)}
        />
      )
    }

    const textTypography = (
      <Typography style={ellipsisStyle} sx={typographySxProps}>
        {text ?? 'Not set'}
      </Typography>
    )

    if (url) {
      return (
        <span style={ellipsisStyle}>
          <LoadedLink
            to={url}
            openNewTab={openNewTab ?? true}
            style={{ width: '100%', ...ellipsisStyle }}
          >
            {text ?? 'Not set'}
          </LoadedLink>
        </span>
      )
    }

    return textTypography
  }

  return (
    <Tooltip arrow={arrow} {...tooltipProps}>
      {children ?? renderTooltipContent()}
    </Tooltip>
  )
}

export default LoadedTooltip
